import { useEffect, useRef } from 'react';

import maplibregl, { Map as MapLibreMap } from 'maplibre-gl';

import { REGION_BORDER, REGION_FILL, REGION_HOVER, REGION_LABEL } from '../CountryView/constants';

// World/outline layer ids declared by sibling unified modules — kept local
// to avoid a runtime import cycle.
const WORLD_FILL_LAYER = 'countries-fill';
const WORLD_LABEL_LAYER = 'countries-label';
const OUTLINE_LAYER = 'country-outline-line';

// Must match the world FILL_LAYER paint set up in UnifiedMap so subsequent
// zoom changes resume the zoom-based fade once we're back on the world view.
const WORLD_FILL_OPACITY_EXPR: maplibregl.ExpressionSpecification = [
  'interpolate',
  ['linear'],
  ['zoom'],
  2,
  0.75,
  5,
  0.3,
];

// Same expression the WORLD_LABEL_LAYER layer was created with. Restored
// after drill-out so the next drill-in / zoom keeps the zoom-driven label
// fade (0 below z2, 1 by z2.5) instead of getting stuck at scalar 1.
const WORLD_LABEL_TEXT_OPACITY_EXPR: maplibregl.ExpressionSpecification = [
  'interpolate',
  ['linear'],
  ['zoom'],
  2,
  0,
  2.5,
  1,
];

// Same expression the REGION_LABEL layer was created with. Restored after
// drill-out so the next drill-in gets zoom-driven label fade for free.
const REGION_LABEL_TEXT_OPACITY_EXPR: maplibregl.ExpressionSpecification = [
  'interpolate',
  ['linear'],
  ['zoom'],
  3,
  0,
  3.5,
  1,
];

const CROSSFADE_DURATION = 600;
const CAMERA_DURATION = 900;
const WORLD_MIN_ZOOM = 1;
const ZOOM_OUT_DELTA = 2.5;
const ZOOM_OUT_FLOOR = 2;

interface UseUnifiedDrillOutOptions {
  mapRef: React.MutableRefObject<MapLibreMap | null>;
  ready: boolean;
  activeCountry: string | null;
}

/**
 * Drill-out (REFACTOR_PLAN.md step 4). Mirrors useUnifiedDrillIn but in
 * reverse: when the user navigates back to the world view, region/outline
 * layers fade out, the world fill fades back in, and the camera eases
 * outward from where the user is (zoom-out on the spot — never a jump to
 * the world centroid). Once the 600 ms crossfade completes, the country
 * layers are hidden again so they're ready for the next drill-in.
 *
 * Triggered only on the country → null transition; staying on the world
 * view or jumping country→country leaves drill-in in charge.
 */
export function useUnifiedDrillOut({ mapRef, ready, activeCountry }: UseUnifiedDrillOutOptions) {
  const prevCountryRef = useRef<string | null>(null);

  useEffect(() => {
    const wasCountry = prevCountryRef.current;
    prevCountryRef.current = activeCountry;

    const map = mapRef.current;
    if (!map || !ready) return;

    // Only react to the explicit country → null transition.
    if (wasCountry === null || activeCountry !== null) return;

    // 1. Restore the world minZoom up front so the camera ease isn't clamped.
    map.setMinZoom(WORLD_MIN_ZOOM);

    // 2. Transitions — set defensively in case the previous drill-in path
    //    didn't (e.g. user clicked Back before drill-in fired).
    map.setPaintProperty(WORLD_FILL_LAYER, 'fill-opacity-transition', {
      duration: CROSSFADE_DURATION,
    });
    map.setPaintProperty(REGION_FILL, 'fill-opacity-transition', {
      duration: CROSSFADE_DURATION,
    });
    map.setPaintProperty(REGION_BORDER, 'line-opacity-transition', {
      duration: CROSSFADE_DURATION,
    });
    map.setPaintProperty(OUTLINE_LAYER, 'line-opacity-transition', {
      duration: CROSSFADE_DURATION,
    });
    map.setPaintProperty(REGION_LABEL, 'text-opacity-transition', {
      duration: CROSSFADE_DURATION,
    });
    map.setPaintProperty(WORLD_LABEL_LAYER, 'text-opacity-transition', {
      duration: CROSSFADE_DURATION,
    });

    // 3. Reverse crossfade — country layers down, world fill back up.
    //    Scalar 0.75 mirrors the legacy WorldView return state; the actual
    //    zoom-based interpolation is restored at the end of the fade so
    //    subsequent zoom changes feel natural.
    map.setPaintProperty(REGION_FILL, 'fill-opacity', 0);
    map.setPaintProperty(REGION_BORDER, 'line-opacity', 0);
    map.setPaintProperty(OUTLINE_LAYER, 'line-opacity', 0);
    map.setPaintProperty(REGION_LABEL, 'text-opacity', 0);
    map.setPaintProperty(WORLD_FILL_LAYER, 'fill-opacity', 0.75);
    // World country labels — fade back in alongside the fill (drill-in
    // had set this to scalar 0). The zoom-interpolated expression is
    // restored after the fade settles, in the timeout cleanup below.
    map.setPaintProperty(WORLD_LABEL_LAYER, 'text-opacity', 1);

    // 4. Camera — zoom-out at the user's current center, never a jump to
    //    the world centroid. Floor at zoom 2 so neighbours stay visible.
    const center = map.getCenter();
    const zoom = Math.max(map.getZoom() - ZOOM_OUT_DELTA, ZOOM_OUT_FLOOR);
    map.easeTo({
      center: [center.lng, center.lat],
      zoom,
      duration: CAMERA_DURATION,
      essential: true,
    });

    // 5. After the crossfade settles, hide the country layers (so they're
    //    ready to be re-shown by the next drill-in) and restore the world
    //    fill's zoom-based opacity interpolation + the region label's
    //    zoom-based text-opacity interpolation. setTimeout is cancelled if
    //    activeCountry changes again before it fires.
    const timer = window.setTimeout(() => {
      for (const id of [REGION_FILL, REGION_BORDER, REGION_LABEL, REGION_HOVER]) {
        if (map.getLayer(id)) map.setLayoutProperty(id, 'visibility', 'none');
      }
      if (map.getLayer(WORLD_FILL_LAYER)) {
        map.setPaintProperty(WORLD_FILL_LAYER, 'fill-opacity', WORLD_FILL_OPACITY_EXPR);
      }
      if (map.getLayer(REGION_LABEL)) {
        map.setPaintProperty(REGION_LABEL, 'text-opacity', REGION_LABEL_TEXT_OPACITY_EXPR);
      }
      if (map.getLayer(WORLD_LABEL_LAYER)) {
        map.setPaintProperty(WORLD_LABEL_LAYER, 'text-opacity', WORLD_LABEL_TEXT_OPACITY_EXPR);
      }
    }, CROSSFADE_DURATION + 50);

    return () => {
      window.clearTimeout(timer);
    };
  }, [mapRef, ready, activeCountry]);
}
